import { openSafeExternalUrl } from '../../../utils/safeNavigation';
import { brandPalette as palette, getContrastText, getReadableColor, getGradientContrastText } from '../../../utils/brandPalette';
// src/components/common/Header/Header.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Badge,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    Box,
    Container,
    Tooltip,
    Chip,
    Popover,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    InputBase,
    styled,
    Fade,
    CircularProgress,
    Paper,
    Stack,
} from '@mui/material';
import {
    Event as EventIcon,
    Business as BusinessIcon,
    Notifications as NotificationsIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    CalendarToday as CalendarIcon,
    Search as SearchIcon,
    FiberManualRecord as LiveIcon,
    CheckCircle as CheckCircleIcon,
    Menu as MoreVertIcon,
    Group as GroupIcon,
    AutoAwesome as AiIcon,
    Email as InboxIcon,
    Person as AccountCircleIcon,
    RocketLaunch as RocketLaunchIcon,
    DashboardRounded as DashboardCustomizeIcon,
    PeopleAlt as PeopleAltIcon,
    Close as CloseIcon,
    Assessment as AssessmentIcon,
    CampaignRounded as AnnouncementIcon,
    CampaignRounded as CampaignIcon,
    Info as InfoIcon,
    Handshake as ConnectIcon,
    Recommend as ThumbUpIcon,
    TrendingDown as ThumbDownIcon,
    Pending as PendingIcon,
    Schedule as ScheduleIcon,
    VideoCall as VideoCallIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../auth/AuthContext';
import BrandLogo from '../BrandLogo';
import { companyMediaCacheKey, getCompanyLogoUrl, resolveCompanyId } from '../../../utils/companyIdentity';
import { db } from '../../../config/firebase';
import {
    collection,
    query,
    where,
    getDocs,
    onSnapshot,
    orderBy,
    limit,
    doc,
    updateDoc,
    addDoc,
    serverTimestamp,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import './Header.css';

// WhatsApp Icon component
const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="#8BF0CA">
        <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 2.084.89 3.15.892h.002c3.18 0 5.767-2.587 5.768-5.766.001-3.187-2.587-5.734-5.753-5.734zM12.031 19.68h-.002c-1.282 0-2.534-.352-3.602-.969l-.258-.154-1.6.421.427-1.558-.168-.267c-.69-1.1-1.055-2.392-1.054-3.716.001-3.721 3.028-6.749 6.752-6.749 3.724 0 6.752 3.028 6.752 6.752-.001 3.722-3.03 6.75-6.753 6.75zm3.639-5.057c-.18-.09-1.063-.525-1.227-.585-.165-.06-.285-.09-.405.09-.12.18-.465.57-.57.705-.105.12-.21.135-.39.045-.18-.09-.76-.28-1.447-.892-.535-.477-.896-1.066-1.001-1.246-.105-.18-.011-.277.079-.366.08-.08.18-.21.27-.315.09-.105.12-.18.18-.3.06-.12.03-.225-.015-.315-.045-.09-.405-1.003-.555-1.363-.136-.325-.274-.286-.39-.286-.135 0-.285-.014-.405-.014-.285 0-.615.09-.93.405-.315.315-1.2 1.17-1.2 2.85s1.23 3.307 1.398 3.535c.168.228 2.277 3.556 5.448 4.815 3.171 1.26 3.171.84 3.742.787.57-.053 1.84-.75 2.099-1.474.259-.724.259-1.345.181-1.474-.078-.129-.285-.207-.465-.297z"/>
    </svg>
);

// ============================================
// DESIGN TOKENS - Enhanced Glassmorphism
// ============================================



const GLASS_HEADER = {
    background: 'rgba(255,253,250,.96)',
    backdropFilter: 'blur(18px) saturate(125%)',
    WebkitBackdropFilter: 'blur(18px) saturate(125%)',
    border: '1px solid rgba(31,62,82,.10)',
    boxShadow: '0 5px 18px rgba(25,57,78,.065)',
};

const GLASS_PILL = {
    background: 'rgba(255,255,255,.72)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(31,62,82,.09)',
    borderRadius: '9px',
};

const GLASS_POPOVER = {
    background: 'rgba(255,253,250,.985)',
    backdropFilter: 'blur(20px) saturate(130%)',
    WebkitBackdropFilter: 'blur(20px) saturate(130%)',
    border: '1px solid rgba(31,62,82,.10)',
    boxShadow: '0 20px 58px rgba(18,45,64,.16)',
};

// ============================================
// ALPHA HELPER
// ============================================

const rgba = (color, opacity) => {
    if (!color) return `rgba(0,0,0,${opacity})`;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// ============================================
// GLASS POD FUNCTION
// ============================================

const glassPod = (color, size = 44, radius = '13px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
    background: `linear-gradient(135deg, ${color}, ${rgba(color, .72)})`,
    border: '1px solid rgba(255,255,255,.45)',
    boxShadow: `0 8px 20px ${rgba(color, .22)}`,
});

// ============================================
// Motion Components
// ============================================

const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionIconButton = motion(IconButton);

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: '999px',
    backgroundColor: 'rgba(249,245,239,.92)',
    backdropFilter: 'blur(8px) saturate(140%)',
    WebkitBackdropFilter: 'blur(8px) saturate(140%)',
    border: '1px solid rgba(31,62,82,.075)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.18)',
        borderColor: 'rgba(255,255,255,0.3)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08), 0 4px 24px rgba(0,0,0,0.08)',
    },
    '&:focus-within': {
        backgroundColor: 'rgba(255,255,255,0.22)',
        borderColor: 'rgba(255,255,255,0.4)',
        boxShadow: `inset 0 1px 2px rgba(0,0,0,0.08), 0 0 0 4px rgba(255,255,255,0.12), 0 4px 32px rgba(0,0,0,0.1)`,
    },
    maxWidth: '420px',
    flex: 1,
    margin: '0 16px',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 1.5),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: '#0f172a',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1.5, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4.5)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        fontSize: '0.85rem',
        fontWeight: 500,
        '&::placeholder': {
            color: '#94a3b8',
            opacity: 1,
            fontWeight: 400,
        },
        '&:focus': {
            color: '#0f172a',
        },
    },
}));

// ============================================
// LOGO MARK
// ============================================

const LogoMark = () => (
    <Box
        component={motion.div}
        whileHover={{ y: -1 }}
        sx={{
            width: 30,
            height: 30,
            borderRadius: '9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 0.9,
            position: 'relative',
            background: 'linear-gradient(145deg, var(--primary), var(--secondary))',
            border: '1px solid rgba(var(--primary-rgb),.20)',
            boxShadow: '0 4px 10px rgba(var(--primary-rgb),.16)',
            flexShrink: 0,
        }}
    >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
            <path d="M4 7L12 3L20 7V12C20 16.5 16.9 20 12 21C7.1 20 4 16.5 4 12V7Z" stroke="var(--brand-action-text)" strokeWidth="1.7" strokeLinejoin="round" fill="rgba(255,255,255,0.08)" />
            <path d="M8.5 12.2L11 14.6L15.7 9.6" stroke="var(--brand-action-text)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </Box>
);

// ============================================
// GLASS NAV PILL
// ============================================

const GlassNavPill = ({ label, icon, path, active, onClick }) => (
    <Button
        component={Link}
        to={path}
        disableRipple
        startIcon={icon}
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        sx={{
            borderRadius: '9px',
            textTransform: 'none',
            fontWeight: active ? 700 : 600,
            fontSize: '0.69rem',
            px: 1.05,
            py: 0.55,
            minWidth: 'auto',
            minHeight: 31,
            color: active ? palette.rust : palette.inkSoft,
            position: 'relative',
            zIndex: 1,
            whiteSpace: 'nowrap',
            transition: 'background .18s ease, color .18s ease, border-color .18s ease',
            border: active ? `1px solid ${rgba(palette.rust, .13)}` : '1px solid transparent',
            background: active ? rgba(palette.rust, .075) : 'transparent',
            boxShadow: active ? '0 2px 7px rgba(25,57,78,.035)' : 'none',
            '& .MuiButton-startIcon': { marginRight: '5px', color: 'inherit' },
            '& .MuiSvgIcon-root': { fontSize: '15px' },
            '&:hover': {
                color: getReadableColor(palette.rust),
                background: active ? rgba(palette.rust, .09) : rgba(palette.rust, .045),
                transform: 'none',
            },
        }}
    >
        {label}
    </Button>
);

// ============================================
// NOTIFICATION LIST ITEM
// ============================================

const NotificationListItem = ({ notification, onAction, onAcceptDecline, formatTime }) => {
    const getNotificationIcon = () => {
        if (notification.icon) return notification.icon;

        switch (notification.type) {
            case 'invitation':
                return <InboxIcon sx={{ fontSize: 16, color: '#fff' }} />;
            case 'connection_request':
            case 'user_connection':
                return <ConnectIcon sx={{ fontSize: 16, color: '#fff' }} />;
            case 'invitation_accepted':
            case 'ai_accepted':
            case 'connection_accepted':
                return <CheckCircleIcon sx={{ fontSize: 16, color: '#fff' }} />;
            case 'ai_sent':
                return <AiIcon sx={{ fontSize: 16, color: '#fff' }} />;
            case 'meeting_request':
            case 'view_meeting':
                return <VideoCallIcon sx={{ fontSize: 16, color: '#fff' }} />;
            case 'meeting_scheduled':
                return <ScheduleIcon sx={{ fontSize: 16, color: '#fff' }} />;
            case 'push_notification':
            case 'announcement':
                return <CampaignIcon sx={{ fontSize: 16, color: '#fff' }} />;
            case 'info':
                return <InfoIcon sx={{ fontSize: 16, color: '#fff' }} />;
            default:
                return <NotificationsIcon sx={{ fontSize: 16, color: '#fff' }} />;
        }
    };

    const getNotificationColor = () => {
        switch (notification.type) {
            case 'invitation':
            case 'connection_request':
            case 'user_connection':
                return palette.rust;
            case 'invitation_accepted':
            case 'ai_accepted':
            case 'connection_accepted':
                return palette.success;
            case 'ai_sent':
                return palette.purple;
            case 'meeting_request':
            case 'view_meeting':
            case 'meeting_scheduled':
                return palette.teal;
            case 'push_notification':
            case 'announcement':
                return palette.gold;
            case 'error':
                return palette.danger;
            case 'warning':
                return palette.warning;
            default:
                return palette.rust;
        }
    };

    const color = getNotificationColor();

    return (
        <ListItemButton
            component="div"
            onClick={() => onAction(notification)}
            sx={{
                py: 1.5,
                px: 2,
                bgcolor: notification.read ? 'transparent' : `${palette.rust}06`,
                borderBottom: `1px solid ${palette.hairline}`,
                transition: 'background-color 0.2s ease',
                '&:hover': { backgroundColor: `${palette.rust}08` },
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
            }}
        >
            <ListItemAvatar sx={{ minWidth: 36 }}>
                {/*
                    FIX: this used to be `backgroundColor: `${color}18`` (a ~9%
                    opacity tint — nearly white) paired with a hard-coded white
                    icon from getNotificationIcon(). White-on-near-white made
                    every notification icon invisible. A solid gradient fill
                    (same recipe as `glassPod` above) keeps the icon's white
                    fill readable against real contrast.
                */}
                <Avatar sx={{
                    background: `linear-gradient(135deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
                    width: 36,
                    height: 36,
                    boxShadow: `0 2px 10px ${rgba(color, 0.35)}, inset 0 1px 0 rgba(255,255,255,0.35)`,
                }}>
                    {getNotificationIcon()}
                </Avatar>
            </ListItemAvatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography component="span" sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.85rem' }}>
                        {notification.title}
                    </Typography>
                    {!notification.read && (
                        <Chip
                            label="New"
                            size="small"
                            sx={{
                                height: 16,
                                fontSize: '0.5rem',
                                bgcolor: `${palette.rust}15`,
                                color: getReadableColor(palette.rust),
                                fontWeight: 600,
                                borderRadius: '4px',
                            }}
                        />
                    )}
                </Box>
                <Typography component="div" sx={{ color: 'rgba(0,0,0,0.6)', fontSize: '0.8rem', mt: 0.25 }}>
                    {notification.message}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography component="span" sx={{ color: 'rgba(0,0,0,0.3)', fontSize: '0.65rem' }}>
                        {formatTime(notification.time)}
                    </Typography>
                    {notification.action === 'accept_decline' && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                                size="small"
                                variant="contained"
                                sx={{
                                    fontSize: '0.55rem',
                                    py: 0.15,
                                    px: 1.2,
                                    minWidth: 'auto',
                                    bgcolor: getReadableColor(palette.rust),
                                    borderRadius: '6px',
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: palette.rustDeep }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAcceptDecline(notification, 'accept');
                                }}
                            >
                                <ThumbUpIcon sx={{ fontSize: 10, mr: 0.5 }} />
                                Accept
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                sx={{
                                    fontSize: '0.55rem',
                                    py: 0.15,
                                    px: 1.2,
                                    minWidth: 'auto',
                                    borderColor: 'rgba(239,68,68,0.3)',
                                    color: '#ef4444',
                                    borderRadius: '6px',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAcceptDecline(notification, 'decline');
                                }}
                            >
                                <ThumbDownIcon sx={{ fontSize: 10, mr: 0.5 }} />
                                Decline
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </ListItemButton>
    );
};

// ============================================
// HEADER COMPONENT
// ============================================

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, logout, userData, companyData, userRole, isTeamMember, isCompanyAdmin } = useAuth();
    const [notificationAnchor, setNotificationAnchor] = useState(null);
    const [profileAnchor, setProfileAnchor] = useState(null);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [messageCount, setMessageCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [scrolled, setScrolled] = useState(false);
    const [loadingNotifications, setLoadingNotifications] = useState(true);
    const audioContextRef = useRef(null);

    // ============================================
    // SCROLL EFFECT
    // ============================================

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // ============================================
    // GET USER COMPANY ID
    // ============================================

    const currentCompanyId = resolveCompanyId({ companyData, userData, currentUser });
    const getCompanyId = () => currentCompanyId;

    const isReadForViewer = (data = {}) => {
        const uid = currentUser?.uid;
        if (data.read === true) return true;
        return Boolean(uid && Array.isArray(data.readBy) && data.readBy.includes(uid));
    };

    const persistNotificationsRead = async (items = []) => {
        const uid = currentUser?.uid;
        const pending = items.filter((item) => item && item.source && !item.read);
        if (!uid || !pending.length) return;
        for (let offset = 0; offset < pending.length; offset += 450) {
            const batch = writeBatch(db);
            pending.slice(offset, offset + 450).forEach((item) => {
                batch.update(doc(db, item.source, item.id), { readBy: arrayUnion(uid) });
            });
            await batch.commit();
        }
    };

    const isTeamMemberRole = userRole === 'employee' || userRole === 'manager' || userRole === 'viewer';
    const resolvedCompanyLogo = getCompanyLogoUrl(companyData || {}) || '';
    const companyCacheKey = currentCompanyId ? companyMediaCacheKey(currentCompanyId, 'logo') : '';
    const [companyLogo, setCompanyLogo] = useState(() => resolvedCompanyLogo || '');
    const companyDisplayName = companyData?.companyName || userData?.companyName || 'TELCOFAIR';

    useEffect(() => {
        const scopedCached = typeof window !== 'undefined' && companyCacheKey ? (localStorage.getItem(companyCacheKey) || '') : '';
        const hasLoadedCompany = Boolean(companyData && resolveCompanyId(companyData));
        const next = resolvedCompanyLogo || (hasLoadedCompany ? '' : scopedCached);
        setCompanyLogo(next);
        if (typeof window !== 'undefined' && companyCacheKey) {
            if (resolvedCompanyLogo) localStorage.setItem(companyCacheKey, resolvedCompanyLogo);
            else if (hasLoadedCompany || !next) localStorage.removeItem(companyCacheKey);
        }
    }, [resolvedCompanyLogo, companyCacheKey, companyData]);

    useEffect(() => {
        const handleLogo = (event) => {
            const eventCompanyId = event?.detail?.companyId;
            if (eventCompanyId && currentCompanyId && String(eventCompanyId) !== String(currentCompanyId)) return;
            const next = event?.detail?.logoUrl || '';
            setCompanyLogo(next);
            if (typeof window !== 'undefined' && companyCacheKey) {
                if (next) localStorage.setItem(companyCacheKey, next);
                else localStorage.removeItem(companyCacheKey);
            }
        };
        window.addEventListener('telcofair-company-logo-updated', handleLogo);
        return () => window.removeEventListener('telcofair-company-logo-updated', handleLogo);
    }, [currentCompanyId, companyCacheKey]);

    // ============================================
    // NAVIGATION ITEMS
    // ============================================

    const getNavItems = () => {
        const items = [
            {
                label: 'Dashboard',
                icon: <DashboardCustomizeIcon sx={{ fontSize: 19 }} />,
                path: '/dashboard',
            },
            {
                label: 'Meetings',
                icon: <EventIcon sx={{ fontSize: 19 }} />,
                path: '/dashboard/meetings',
            },
            {
                label: 'Schedule',
                icon: <CalendarIcon sx={{ fontSize: 19 }} />,
                path: '/dashboard/meetings/schedule',
            },
            {
                label: 'Companies',
                icon: <BusinessIcon sx={{ fontSize: 19 }} />,
                path: '/dashboard/companies',
            },
            {
                label: 'People',
                icon: <PeopleAltIcon sx={{ fontSize: 19 }} />,
                path: '/dashboard/users',
            },
        ];

        if (!isTeamMemberRole && (isCompanyAdmin || userRole === 'admin')) {
            items.push({
                label: 'Team',
                icon: <GroupIcon sx={{ fontSize: 19 }} />,
                path: '/dashboard/company/team',
            });
        }

        items.push({
            label: 'Reports',
            icon: <AssessmentIcon sx={{ fontSize: 19 }} />,
            path: '/dashboard/reports',
        });

        return items;
    };

    const navItems = getNavItems();

    const sidebarSections = [
        {
            label: 'Overview',
            items: [
                { label: 'Dashboard', icon: <DashboardCustomizeIcon sx={{ fontSize: 18 }} />, path: '/dashboard' },
                { label: 'Notifications', icon: <NotificationsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/notifications' },
                { label: 'AI Insights', icon: <AiIcon sx={{ fontSize: 18 }} />, path: '/dashboard/company/ai-insights' },
                { label: 'Analytics', icon: <AssessmentIcon sx={{ fontSize: 18 }} />, path: '/dashboard/company/analytics' },
            ],
        },
        {
            label: 'Network',
            items: [
                { label: 'Companies', icon: <BusinessIcon sx={{ fontSize: 18 }} />, path: '/dashboard/companies?tab=companies', activePath: '/dashboard/companies', activeTab: 'companies' },
                { label: 'People', icon: <PeopleAltIcon sx={{ fontSize: 18 }} />, path: '/dashboard/users' },
                { label: 'Invitations', icon: <InboxIcon sx={{ fontSize: 18 }} />, path: '/dashboard/companies?tab=invitations', activePath: '/dashboard/companies', activeTab: 'invitations' },
                { label: 'Messages', icon: <InboxIcon sx={{ fontSize: 18 }} />, path: '/dashboard/messages' },
            ],
        },
        {
            label: 'Meetings',
            items: [
                { label: 'Meeting Center', icon: <EventIcon sx={{ fontSize: 18 }} />, path: '/dashboard/meetings' },
                { label: 'Schedule Meeting', icon: <CalendarIcon sx={{ fontSize: 18 }} />, path: '/dashboard/meetings/schedule' },
            ],
        },
        {
            label: 'Company',
            items: [
                ...(!isTeamMemberRole && (isCompanyAdmin || userRole === 'admin') ? [{ label: 'Team', icon: <GroupIcon sx={{ fontSize: 18 }} />, path: '/dashboard/company/team' }] : []),
                { label: 'Reports', icon: <AssessmentIcon sx={{ fontSize: 18 }} />, path: '/dashboard/reports' },
                { label: 'Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/company/settings' },
            ],
        },
        {
            label: 'Growth & Help',
            items: [
                { label: 'Ad Campaigns', icon: <CampaignIcon sx={{ fontSize: 18 }} />, path: '/dashboard/ads' },
                { label: 'Support', icon: <ConnectIcon sx={{ fontSize: 18 }} />, path: '/dashboard/support' },
            ],
        },
    ];

    const sidebarItems = sidebarSections.flatMap((section) => section.items);
    const sidebarColors = ['#2F80ED', '#8B5CF6', '#13A7D8', '#F39A24', '#13A773', '#E84B7A', '#0F9E9A', '#7C5CF2', '#F06A5F', '#C047A8', '#2E6F8E', '#42B89B'];

    // ============================================
    // IS ACTIVE HELPER
    // ============================================

    const isActive = (path, item = {}) => {
        const pathOnly = item.activePath || String(path || '').split('?')[0];
        if (pathOnly === '/dashboard') return location.pathname === '/dashboard';
        const pathMatches = location.pathname === pathOnly || location.pathname.startsWith(pathOnly + '/');
        if (!pathMatches) return false;
        if (item.activeTab && pathOnly === '/dashboard/companies') {
            const currentTab = new URLSearchParams(location.search).get('tab') || 'companies';
            if (item.activeTab === 'companies') return currentTab !== 'invitations';
            return currentTab === item.activeTab;
        }
        return true;
    };

    const activeSidebarItem = sidebarItems.find((item) => isActive(item.path, item)) || sidebarItems[0];
    const currentPageLabel = activeSidebarItem?.label || 'Dashboard';

    // ============================================
    // PLAY NOTIFICATION SOUND
    // ============================================

    const playNotificationSound = () => {
        try {
            // Browsers block AudioContext until the user has interacted with the page.
            // Skip notification audio before activation instead of creating a suspended context and logging warnings.
            if (typeof window === 'undefined' || !window.navigator?.userActivation?.hasBeenActive) return;
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }

            if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
            }

            const oscillator = audioContextRef.current.createOscillator();
            const gainNode = audioContextRef.current.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContextRef.current.destination);

            oscillator.frequency.setValueAtTime(800, audioContextRef.current.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(1200, audioContextRef.current.currentTime + 0.15);
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.25, audioContextRef.current.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.3);

            oscillator.start(audioContextRef.current.currentTime);
            oscillator.stop(audioContextRef.current.currentTime + 0.3);

            setTimeout(() => {
                const osc2 = audioContextRef.current.createOscillator();
                const gain2 = audioContextRef.current.createGain();
                osc2.connect(gain2);
                gain2.connect(audioContextRef.current.destination);
                osc2.frequency.value = 1000;
                osc2.type = 'sine';
                gain2.gain.setValueAtTime(0.15, audioContextRef.current.currentTime);
                gain2.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.2);
                osc2.start(audioContextRef.current.currentTime);
                osc2.stop(audioContextRef.current.currentTime + 0.2);
            }, 200);

        } catch (error) {
            // Silently fail
        }
    };

    // ============================================
    // FETCH UNREAD MESSAGES COUNT
    // ============================================

    useEffect(() => {
        const fetchMessageCount = async () => {
            try {
                const companyId = getCompanyId();
                if (!companyId) return;

                const invitationsRef = collection(db, 'invitations');
                const q = query(invitationsRef, where('targetCompanyId', '==', companyId));
                const snapshot = await getDocs(q);

                let totalUnread = 0;
                const companyIds = [];

                snapshot.forEach((doc) => {
                    const data = doc.data();
                    if (!['accepted', 'connected'].includes(data.status)) return;
                    if (data.companyId) companyIds.push(data.companyId);
                });

                const sentQuery = query(invitationsRef, where('companyId', '==', companyId));
                const sentSnapshot = await getDocs(sentQuery);
                sentSnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (!['accepted', 'connected'].includes(data.status)) return;
                    if (data.targetCompanyId && !companyIds.includes(data.targetCompanyId)) {
                        companyIds.push(data.targetCompanyId);
                    }
                });

                for (const targetId of companyIds) {
                    const chatId = [companyId, targetId].sort().join('_');
                    const chatRef = collection(db, 'chats', chatId, 'messages');
                    const unreadQuery = query(
                        chatRef,
                        where('read', '==', false),
                        where('senderId', '!=', companyId)
                    );
                    const unreadSnapshot = await getDocs(unreadQuery);
                    totalUnread += unreadSnapshot.size;
                }

                setMessageCount(totalUnread);

            } catch (error) {
                console.error('Error fetching message count:', error);
            }
        };

        fetchMessageCount();

        const companyId = getCompanyId();
        if (companyId) {
            const invitationsRef = collection(db, 'invitations');
            const receivedQ = query(invitationsRef, where('targetCompanyId', '==', companyId));
            const sentQ = query(invitationsRef, where('companyId', '==', companyId));
            const unsubscribeReceived = onSnapshot(receivedQ, () => { fetchMessageCount(); });
            const unsubscribeSent = onSnapshot(sentQ, () => { fetchMessageCount(); });
            return () => {
                unsubscribeReceived();
                unsubscribeSent();
            };
        }
    }, [companyData, userData, currentUser]);

    // ============================================
    // FETCH NOTIFICATIONS
    // ============================================

    const fetchNotifications = async () => {
        try {
            setLoadingNotifications(true);
            const companyId = getCompanyId();
            const targetId = companyId || currentUser?.uid;

            if (!targetId) {
                setNotifications([]);
                setUnreadCount(0);
                setLoadingNotifications(false);
                return;
            }

            const allNotifications = [];

            // 1. Fetch Company Invitations (Connection Requests)
            try {
                const invitationsRef = collection(db, 'invitations');
                const receivedQuery = query(
                    invitationsRef,
                    where('targetCompanyId', '==', targetId),
                    orderBy('createdAt', 'desc'),
                    limit(20)
                );
                const receivedSnapshot = await getDocs(receivedQuery);

                receivedSnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.status === 'pending') {
                        allNotifications.push({
                            id: doc.id,
                            title: '📨 Connection Request',
                            message: `${data.companyName || 'A company'} wants to connect with you`,
                            time: data.createdAt?.toDate?.() || new Date(),
                            type: 'connection_request',
                            read: isReadForViewer(data),
                            icon: <ConnectIcon sx={{ fontSize: 16, color: '#fff' }} />,
                            data: data,
                            action: 'accept_decline',
                            source: 'invitations',
                            priority: 'high',
                        });
                    }
                });
            } catch (err) {
                console.warn('Error fetching connection requests:', err);
            }

            // 2. Fetch AI Invitations
            try {
                const invitationsRef = collection(db, 'invitations');
                const sentQuery = query(
                    invitationsRef,
                    where('companyId', '==', targetId),
                    where('autoGenerated', '==', true),
                    orderBy('createdAt', 'desc'),
                    limit(20)
                );
                const sentSnapshot = await getDocs(sentQuery);

                sentSnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.status === 'pending') {
                        allNotifications.push({
                            id: doc.id,
                            title: '🤖 AI Invitation Sent',
                            message: `AI invitation sent to ${data.targetCompanyName || 'a company'} (${Math.round(data.aiScore || 0)}% match)`,
                            time: data.createdAt?.toDate?.() || new Date(),
                            type: 'ai_sent',
                            read: isReadForViewer(data),
                            icon: <AiIcon sx={{ fontSize: 16, color: '#fff' }} />,
                            data: data,
                            action: 'view',
                            source: 'invitations',
                        });
                    }
                });
            } catch (err) {
                console.warn('Error fetching AI invitations:', err);
            }

            // 3. Fetch Meeting Requests
            try {
                const meetingsRef = collection(db, 'meetings');
                const meetingQuery = query(
                    meetingsRef,
                    where('targetId', '==', targetId),
                    where('status', '==', 'pending'),
                    orderBy('createdAt', 'desc'),
                    limit(20)
                );
                const meetingSnapshot = await getDocs(meetingQuery);

                meetingSnapshot.forEach((doc) => {
                    const data = doc.data();
                    allNotifications.push({
                        id: doc.id,
                        title: '📅 Meeting Request',
                        message: `${data.requesterName || 'Someone'} requested a meeting: "${data.title || 'Untitled'}"`,
                        time: data.createdAt?.toDate?.() || new Date(),
                        type: 'meeting_request',
                        read: isReadForViewer(data),
                        icon: <VideoCallIcon sx={{ fontSize: 16, color: '#fff' }} />,
                        data: data,
                        action: 'view_meeting',
                        source: 'meetings',
                        priority: 'high',
                    });
                });
            } catch (err) {
                console.warn('Error fetching meeting requests:', err);
            }

            // 4. Fetch User Connection Requests
            try {
                const introductionsRef = collection(db, 'introductions');
                const introQuery = query(
                    introductionsRef,
                    where('toUserId', '==', targetId),
                    where('status', '==', 'pending'),
                    orderBy('createdAt', 'desc'),
                    limit(20)
                );
                const introSnapshot = await getDocs(introQuery);

                introSnapshot.forEach((doc) => {
                    const data = doc.data();
                    allNotifications.push({
                        id: doc.id,
                        title: '👤 User Connection Request',
                        message: `${data.fromUserName || 'Someone'} wants to connect with you`,
                        time: data.createdAt?.toDate?.() || new Date(),
                        type: 'user_connection',
                        read: isReadForViewer(data),
                        icon: <PeopleAltIcon sx={{ fontSize: 16, color: '#fff' }} />,
                        data: data,
                        action: 'accept_decline',
                        source: 'introductions',
                        priority: 'high',
                    });
                });
            } catch (err) {
                console.warn('Error fetching user connection requests:', err);
            }

            // 5. Fetch Push Notifications
            try {
                const notificationsRef = collection(db, 'notifications');
                const notificationQuery = query(
                    notificationsRef,
                    where('userId', 'in', ['all', targetId]),
                    orderBy('createdAt', 'desc'),
                    limit(20)
                );
                const notificationSnapshot = await getDocs(notificationQuery);

                notificationSnapshot.forEach((doc) => {
                    const data = doc.data();
                    const isForCompany = data.userId === 'all' || data.userId === targetId;

                    if (isForCompany) {
                        allNotifications.push({
                            id: doc.id,
                            title: data.title || 'New Notification',
                            message: data.message || '',
                            time: data.createdAt?.toDate?.() || new Date(),
                            type: data.type || 'push_notification',
                            read: isReadForViewer(data),
                            icon: <NotificationsIcon sx={{ fontSize: 16, color: '#fff' }} />,
                            data: data,
                            action: 'view_notification',
                            source: 'notifications',
                        });
                    }
                });
            } catch (err) {
                console.warn('Error fetching push notifications:', err);
            }

            allNotifications.sort((a, b) => b.time - a.time);

            const uniqueNotifications = [];
            const seenIds = new Set();
            for (const notif of allNotifications) {
                if (!seenIds.has(notif.id)) {
                    seenIds.add(notif.id);
                    uniqueNotifications.push(notif);
                }
            }

            const limitedNotifications = uniqueNotifications.slice(0, 25);
            setNotifications(limitedNotifications);

            const unread = limitedNotifications.filter(n => !n.read).length;
            setUnreadCount(unread);

        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoadingNotifications(false);
        }
    };

    // Set up real-time listeners
    useEffect(() => {
        fetchNotifications();

        const companyId = getCompanyId();
        const targetId = companyId || currentUser?.uid;

        if (targetId) {
            const invitationsRef = collection(db, 'invitations');
            const inviteQuery = query(
                invitationsRef,
                where('targetCompanyId', '==', targetId)
            );
            const unsubscribeInvites = onSnapshot(inviteQuery, () => {
                fetchNotifications();
                playNotificationSound();
            });

            const meetingsRef = collection(db, 'meetings');
            const meetingQuery = query(
                meetingsRef,
                where('targetId', '==', targetId),
                where('status', '==', 'pending')
            );
            const unsubscribeMeetings = onSnapshot(meetingQuery, () => {
                fetchNotifications();
                playNotificationSound();
            });

            const introductionsRef = collection(db, 'introductions');
            const introQuery = query(
                introductionsRef,
                where('toUserId', '==', targetId),
                where('status', '==', 'pending')
            );
            const unsubscribeIntroductions = onSnapshot(introQuery, () => {
                fetchNotifications();
                playNotificationSound();
            });

            const notificationsRef = collection(db, 'notifications');
            const notifQuery = query(
                notificationsRef,
                where('userId', 'in', ['all', targetId])
            );
            const unsubscribeNotifications = onSnapshot(notifQuery, () => {
                fetchNotifications();
                playNotificationSound();
            });

            return () => {
                unsubscribeInvites();
                unsubscribeMeetings();
                unsubscribeIntroductions();
                unsubscribeNotifications();
            };
        }
    }, [companyData, userData, currentUser]);

    // ============================================
    // HANDLE NOTIFICATION ACTION
    // ============================================

    const handleNotificationAction = async (notification) => {
        try { await persistNotificationsRead([notification]); } catch (error) { console.warn('Could not persist notification read state:', error?.message || error); }
        setNotifications(prev =>
            prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));

        switch (notification.type) {
            case 'invitation':
            case 'connection_request':
            case 'user_connection':
                navigate('/dashboard/companies');
                break;
            case 'meeting_request':
            case 'view_meeting':
            case 'meeting_scheduled':
                navigate('/dashboard/meetings');
                break;
            default:
                if (notification.link) {
                    openSafeExternalUrl(notification.link);
                } else {
                    navigate('/dashboard');
                }
        }
        handleNotificationClose();
    };

    const handleAcceptDecline = async (notification, action) => {
        try {
            const collectionName = notification.type === 'user_connection' ? 'introductions' : 'invitations';
            const docRef = doc(db, collectionName, notification.id);

            await updateDoc(docRef, {
                status: action === 'accept' ? 'accepted' : 'declined',
                updatedAt: serverTimestamp(),
                ...(currentUser?.uid ? { readBy: arrayUnion(currentUser.uid) } : {}),
            });

            toast.success(action === 'accept' ? '✅ Connection accepted!' : '❌ Connection declined');

            setNotifications(prev =>
                prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));

        } catch (error) {
            console.error('Error handling action:', error);
            toast.error('Failed to process request');
        }
    };

    // ============================================
    // HANDLERS
    // ============================================

    const handleProfileOpen = (event) => {
        setProfileAnchor(event.currentTarget);
    };

    const handleProfileClose = () => {
        setProfileAnchor(null);
    };

    const handleNotificationOpen = (event) => {
        setNotificationAnchor(event.currentTarget);
    };

    const handleMarkAllNotificationsRead = async () => {
        const pending = notifications.filter((item) => !item.read);
        if (!pending.length) return;
        try {
            await persistNotificationsRead(pending);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Could not mark all notifications read:', error);
            toast.error('Unable to mark all notifications as read.');
        }
    };

    const handleNotificationClose = () => {
        setNotificationAnchor(null);
    };

    const handleMobileMenuOpen = (event) => {
        setMobileMenuAnchor(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
            toast.success('Logged out successfully');
        } catch (error) {
            toast.error('Logout failed');
        }
    };

    const formatTime = (date) => {
        if (!date) return '';
        const d = date instanceof Date ? date : date.toDate?.() || new Date(date);
        const now = new Date();
        const diff = now - d;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        return d.toLocaleDateString();
    };

    const profileMenuItems = [
        {
            label: 'My Profile',
            icon: <AccountCircleIcon sx={{ fontSize: 20 }} />,
            path: '/dashboard/company-info'
        },
        {
            label: 'Privacy & AI Profile',
            icon: <SettingsIcon sx={{ fontSize: 20 }} />,
            path: '/dashboard/profile/settings'
        },
        {
            label: 'Company Team',
            icon: <GroupIcon sx={{ fontSize: 20 }} />,
            path: '/dashboard/company/team',
            show: !isTeamMemberRole && (isCompanyAdmin || userRole === 'admin')
        },
        {
            label: 'AI Insights',
            icon: <AiIcon sx={{ fontSize: 20 }} />,
            path: '/dashboard/company/ai-insights'
        },
        {
            label: 'Ad Campaigns',
            icon: <RocketLaunchIcon sx={{ fontSize: 20 }} />,
            path: '/dashboard/ads'
        },
        {
            label: 'Reports',
            icon: <AssessmentIcon sx={{ fontSize: 20 }} />,
            path: '/dashboard/reports'
        },
        {
            label: 'Settings',
            icon: <SettingsIcon sx={{ fontSize: 20 }} />,
            path: '/dashboard/company/settings'
        },
    ];

    const headerHeight = scrolled ? 54 : 56;

    return (
        <>
            <Box className="tf-sidebar">
                <Box className="tf-sidebar-brand-card" onClick={() => navigate('/dashboard')}>
                    <Box className="tf-sidebar-logo">
                        <BrandLogo src={companyLogo || ''} scope="company" size={56} radius={14} alt={companyDisplayName} sx={{ width: '100%', height: '100%', boxShadow: 'none', border: 0 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box className="tf-sidebar-brand-title">{companyDisplayName}</Box>
                        <Box className="tf-sidebar-brand-sub">TELCOFAIR BUSINESS NETWORK</Box>
                    </Box>
                </Box>

                <Box className="tf-sidebar-meta">
                    <Box className="tf-sidebar-chip">● TENANT WORKSPACE</Box>
                    <Box className="tf-sidebar-chip success">● {String(userRole || 'Member').replace(/_/g, ' ')}</Box>
                </Box>

                <Box className="tf-sidebar-nav">
                    {sidebarSections.map((section, sectionIndex) => (
                        <Box className="tf-sidebar-nav-section" key={section.label}>
                            <Box className="tf-sidebar-section-label">
                                <Box component="span">{section.label}</Box>
                                <Box className="tf-sidebar-section-rule" />
                            </Box>
                            {section.items.map((item, itemIndex) => {
                                const active = isActive(item.path, item);
                                const moduleColor = sidebarColors[(sectionIndex * 3 + itemIndex) % sidebarColors.length];
                                return (
                                    <Button
                                        key={`${item.path}-${item.label}`}
                                        className={`tf-sidebar-item${active ? ' active' : ''}`}
                                        onClick={() => navigate(item.path)}
                                        startIcon={
                                            <Box className="tf-sidebar-icon" sx={{ '--module-color': moduleColor, '--module-contrast': getContrastText(moduleColor) }}>
                                                {item.icon}
                                            </Box>
                                        }
                                    >
                                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
                                            {item.label}
                                        </Box>
                                        {item.label === 'Messages' && messageCount > 0 && <Box className="tf-sidebar-count">{messageCount > 99 ? '99+' : messageCount}</Box>}
                                        {item.label === 'Notifications' && unreadCount > 0 && <Box className="tf-sidebar-count alert">{unreadCount > 99 ? '99+' : unreadCount}</Box>}
                                    </Button>
                                );
                            })}
                        </Box>
                    ))}
                </Box>

                <Box className="tf-sidebar-user">
                    <Avatar
                        src={userData?.profileImage || currentUser?.photoURL || undefined}
                        sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'var(--secondary)', fontSize: 12, fontWeight: 800 }}
                    >
                        {(userData?.displayName || userData?.firstName || currentUser?.displayName || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#172235', lineHeight: 1.1 }} noWrap>
                            {userData?.displayName || currentUser?.displayName || userData?.firstName || 'Workspace User'}
                        </Typography>
                        <Typography sx={{ mt: .3, fontSize: 8.5, fontWeight: 650, color: '#8290A1', textTransform: 'capitalize' }} noWrap>
                            {String(userRole || 'member').replace(/_/g, ' ')}
                        </Typography>
                    </Box>
                    <Tooltip title="Logout">
                        <IconButton size="small" onClick={handleLogout} sx={{ width: 29, height: 29, borderRadius: '9px', color: '#D94B5A', bgcolor: 'rgba(217,75,90,.07)' }}>
                            <LogoutIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

        <AppBar
            position="fixed"
            color="transparent"
            className="header-appbar"
            elevation={0}
            sx={{
                ...GLASS_HEADER,
                background: `${GLASS_HEADER.background} !important`,
                backgroundColor: `${GLASS_HEADER.background} !important`,
                height: `${headerHeight}px`,
                transition: 'height 0.28s cubic-bezier(.4,0,.2,1), box-shadow 0.28s ease, border-color 0.28s ease',
                overflow: 'visible',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'transparent',
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'transparent',
                    opacity: 0,
                    pointerEvents: 'none',
                },
            }}
        >
            <Container
                maxWidth="xl"
                className="header-container"
                sx={{
                    height: '100%',
                    position: 'relative',
                    background: 'transparent !important',
                    backgroundColor: 'transparent !important',
                }}
            >
                <Toolbar
                    className="header-toolbar"
                    disableGutters
                    sx={{
                        height: '100%',
                        px: { xs: 1, sm: 2 },
                        background: 'transparent !important',
                        backgroundColor: 'transparent !important',
                    }}
                >
                    {/* Compact workspace title — main brand now lives in the sidebar */}
                    <Box
                        className="header-logo"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
                    >
                        <BrandLogo src={companyLogo || ''} scope="company" size={42} radius={11} alt={companyDisplayName} sx={{ boxShadow: '0 4px 10px rgba(25,57,78,.06)', border: '1px solid rgba(var(--primary-rgb),.10)' }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 8, fontWeight: 800, color: '#95A2B1', textTransform: 'uppercase', letterSpacing: '.08em', lineHeight: 1.15 }} noWrap>
                                {companyDisplayName} · Workspace
                            </Typography>
                            <Typography sx={{ mt: .25, fontSize: 12.5, fontWeight: 800, color: '#182235', lineHeight: 1.1 }} noWrap>
                                {currentPageLabel}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Search */}
                    {/* <Search className="header-search">
                        <SearchIconWrapper>
                            <SearchIcon sx={{ fontSize: 18 }} />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Search companies, people, meetings..."
                            inputProps={{ 'aria-label': 'search' }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <IconButton
                                size="small"
                                onClick={() => setSearchQuery('')}
                                sx={{
                                    position: 'absolute',
                                    right: 6,
                                    color: 'rgba(255,255,255,0.6)',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        )}
                        <Box sx={{
                            position: 'absolute',
                            right: 36,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '0.5rem',
                            color: 'rgba(255,255,255,0.3)',
                            fontWeight: 600,
                            letterSpacing: '0.05em',
                            display: { xs: 'none', md: 'block' },
                            background: 'rgba(255,255,255,0.06)',
                            px: 0.8,
                            py: 0.2,
                            borderRadius: '4px',
                            border: '1px solid rgba(255,255,255,0.04)',
                        }}>
                            ⌘K
                        </Box>
                    </Search> */}

                    {/* Desktop Navigation */}
                    <Box className="desktop-nav" sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        gap: 0.15,
                        flex: 1,
                        justifyContent: 'center',
                        position: 'relative',
                        p: 0,
                        borderRadius: '10px',
                        background: 'transparent',
                        border: 'none',
                        minHeight: '34px',
                    }}>
                        {navItems.map((item) => {
                            const active = isActive(item.path);
                            return (
                                <GlassNavPill
                                    key={item.label}
                                    label={item.label}
                                    icon={item.icon}
                                    path={item.path}
                                    active={active}
                                />
                            );
                        })}
                    </Box>

                    {/* Right Section */}
                    <Box className="header-right" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box className="tf-top-quick">
                            <Button onClick={() => navigate('/dashboard/meetings/schedule')} startIcon={<CalendarIcon sx={{ fontSize: 14 }} />} sx={{ '--tf-quick-contrast': getGradientContrastText('#10A7D8','#2889D8'), background: 'linear-gradient(135deg,#10A7D8,#2889D8) !important' }}>Schedule</Button>
                            <Button onClick={() => navigate('/dashboard/companies')} startIcon={<BusinessIcon sx={{ fontSize: 14 }} />} sx={{ '--tf-quick-contrast': getGradientContrastText('#7C5CF2','#A04DE0'), background: 'linear-gradient(135deg,#7C5CF2,#A04DE0) !important' }}>Companies</Button>
                            <Button onClick={() => navigate('/dashboard/messages')} startIcon={<InboxIcon sx={{ fontSize: 14 }} />} sx={{ '--tf-quick-contrast': getGradientContrastText('#F39A24','#E26C2D'), background: 'linear-gradient(135deg,#F39A24,#E26C2D) !important' }}>Messages</Button>
                            <Button onClick={() => navigate('/dashboard/company/ai-insights')} startIcon={<AiIcon sx={{ fontSize: 14 }} />} sx={{ '--tf-quick-contrast': getGradientContrastText('#13A773','#0F9E9A'), background: 'linear-gradient(135deg,#13A773,#0F9E9A) !important' }}>AI Insights</Button>
                        </Box>
                        <Chip
                            icon={
                                <Box
                                    component={motion.span}
                                    
                                    sx={{ display: 'flex' }}
                                >
                                    <LiveIcon sx={{ fontSize: 10, color: '#15803d' }} />
                                </Box>
                            }
                            label="Live"
                            size="small"
                            sx={{
                                ...GLASS_PILL,
                                bgcolor: 'rgba(22,163,74,.09)',
                                color: '#15803d',
                                fontWeight: 700,
                                fontSize: '0.62rem',
                                letterSpacing: '0.04em',
                                height: 26,
                                px: 0.5,
                                border: '1px solid rgba(22,163,74,.14)',
                                display: 'none',
                            }}
                        />

                        <Tooltip title="Notifications">
                            <MotionIconButton
                                onClick={handleNotificationOpen}
                                whileTap={{ scale: 0.92 }}
                                
                                sx={{
                                    borderRadius: '10px',
                                    p: 1,
                                    color: palette.inkLight,
                                    ...GLASS_PILL,
                                    '&:hover': { backgroundColor: 'rgba(var(--primary-rgb),.07)', color: palette.rust },
                                }}
                            >
                                <Badge
                                    badgeContent={unreadCount}
                                    color="error"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                            fontSize: '0.55rem',
                                            height: 18,
                                            minWidth: 18,
                                            fontWeight: 700,
                                            border: '2px solid #fff',
                                            boxShadow: '0 2px 8px rgba(239,68,68,0.4)',
                                        }
                                    }}
                                >
                                    <NotificationsIcon sx={{ fontSize: 22 }} />
                                </Badge>
                            </MotionIconButton>
                        </Tooltip>

                        <Tooltip title="Messages">
                            <MotionIconButton
                                onClick={() => navigate('/dashboard/messages')}
                                whileTap={{ scale: 0.92 }}
                                sx={{
                                    borderRadius: '10px',
                                    p: 1,
                                    color: palette.inkLight,
                                    ...GLASS_PILL,
                                    '&:hover': { backgroundColor: 'rgba(var(--primary-rgb),.07)', color: palette.rust },
                                }}
                            >
                                <Badge
                                    badgeContent={messageCount}
                                    color="success"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            background: `linear-gradient(135deg, ${palette.whatsapp}, #128C7E)`,
                                            fontSize: '0.55rem',
                                            height: 18,
                                            minWidth: 18,
                                            fontWeight: 700,
                                            border: '2px solid #fff',
                                            boxShadow: '0 2px 8px rgba(37,211,102,0.3)',
                                        }
                                    }}
                                >
                                    <WhatsAppIcon />
                                </Badge>
                            </MotionIconButton>
                        </Tooltip>

                        <Tooltip title="Profile">
                            <MotionBox
                                component="button"
                                onClick={handleProfileOpen}
                                whileTap={{ scale: 0.94 }}
                                whileHover={{ scale: 1.05 }}
                                sx={{
                                    ml: 0.5,
                                    p: '2px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                    display: 'flex',
                                    boxShadow: '0 4px 14px rgba(var(--primary-rgb),.20)',
                                }}
                            >
                                <Avatar src={userData?.profileImage || currentUser?.photoURL || undefined} sx={{
                                    width: 34,
                                    height: 34,
                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    border: `2px solid rgba(255,255,255,0.3)`,
                                    color: getContrastText(palette.rust),
                                }}>
                                    {currentUser?.email?.charAt(0).toUpperCase() ||
                                     userData?.companyName?.charAt(0).toUpperCase() ||
                                     'U'}
                                </Avatar>
                            </MotionBox>
                        </Tooltip>

                        <IconButton
                            onClick={handleMobileMenuOpen}
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                borderRadius: '10px',
                                p: 1,
                                color: palette.inkLight,
                                ...GLASS_PILL,
                                '&:hover': { backgroundColor: 'rgba(var(--primary-rgb),.07)', color: palette.rust },
                            }}
                        >
                            <MoreVertIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Box>
                </Toolbar>
            </Container>

            {/* Notification Popover */}
            <Popover
                open={Boolean(notificationAnchor)}
                anchorEl={notificationAnchor}
                onClose={handleNotificationClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: '440px',
                            width: '100%',
                            borderRadius: '20px',
                            ...GLASS_POPOVER,
                            mt: 1.2,
                            overflow: 'hidden',
                        }
                    }
                }}
                TransitionComponent={Fade}
            >
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${palette.hairline}`,
                    background: 'linear-gradient(135deg, rgba(var(--primary-rgb),.055), rgba(var(--accent-rgb),.035))',
                }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700, fontSize: '1rem', color: palette.ink, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsIcon sx={{ fontSize: 20, color: palette.rust }} />
                        Notifications
                        {unreadCount > 0 && (
                            <Chip
                                label={unreadCount}
                                size="small"
                                sx={{
                                    height: 18,
                                    fontSize: '0.6rem',
                                    bgcolor: getReadableColor(palette.rust),
                                    color: '#fff',
                                    fontWeight: 700,
                                }}
                            />
                        )}
                    </Typography>
                    {notifications.length > 0 && (
                        <Button
                            size="small"
                            onClick={handleMarkAllNotificationsRead}
                            sx={{
                                color: getReadableColor(palette.rust),
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'none',
                                borderRadius: '8px',
                                '&:hover': { backgroundColor: `${palette.rust}12` }
                            }}
                        >
                            Mark all read
                        </Button>
                    )}
                </Box>
                <List sx={{ maxHeight: '420px', overflowY: 'auto', p: 0 }}>
                    {loadingNotifications ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <CircularProgress size={32} sx={{ color: palette.rust }} />
                            <Typography component="div" sx={{ color: 'rgba(0,0,0,0.4)', mt: 2, fontSize: '0.85rem' }}>
                                Loading notifications...
                            </Typography>
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Box sx={{
                                ...glassPod(palette.inkFaint, 64),
                                mx: 'auto',
                                mb: 1.5,
                                opacity: 0.5,
                            }}>
                                <NotificationsIcon sx={{ fontSize: 30 }} />
                            </Box>
                            <Typography component="div" sx={{ color: 'rgba(0,0,0,0.4)', mt: 1, fontSize: '0.9rem', fontWeight: 600 }}>
                                All caught up!
                            </Typography>
                            <Typography component="div" sx={{ color: 'rgba(0,0,0,0.25)', fontSize: '0.8rem' }}>
                                No new notifications
                            </Typography>
                        </Box>
                    ) : (
                        notifications.map((notification) => (
                            <NotificationListItem
                                key={notification.id}
                                notification={notification}
                                onAction={handleNotificationAction}
                                onAcceptDecline={handleAcceptDecline}
                                formatTime={formatTime}
                            />
                        ))
                    )}
                </List>
                {notifications.length > 0 && (
                    <Box sx={{ p: 1.5, borderTop: `1px solid ${palette.hairline}`, background: 'rgba(255,255,255,0.6)' }}>
                        <Button
                            fullWidth
                            onClick={() => {
                                handleNotificationClose();
                                navigate('/dashboard/notifications');
                            }}
                            sx={{
                                color: getReadableColor(palette.rust),
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                textTransform: 'none',
                                borderRadius: '10px',
                                py: 0.8,
                                '&:hover': { backgroundColor: `${palette.rust}10` }
                            }}
                        >
                            View All Notifications
                        </Button>
                    </Box>
                )}
            </Popover>

            {/* Profile Menu */}
            <Menu
                anchorEl={profileAnchor}
                open={Boolean(profileAnchor)}
                onClose={handleProfileClose}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '20px',
                            ...GLASS_POPOVER,
                            minWidth: 280,
                            mt: 0.6,
                            overflow: 'hidden',
                        }
                    }
                }}
                TransitionComponent={Fade}
            >
                <Box sx={{
                    p: 2.5,
                    background: 'linear-gradient(135deg, rgba(var(--primary-rgb),.065), rgba(var(--accent-rgb),.035))',
                    borderBottom: `1px solid ${palette.hairline}`,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={userData?.profileImage || currentUser?.photoURL || undefined} sx={{
                            width: 50,
                            height: 50,
                            background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                            fontWeight: 700,
                            fontSize: '1.25rem',
                            color: '#fff',
                            boxShadow: '0 4px 16px rgba(var(--primary-rgb),.20)',
                        }}>
                            {currentUser?.email?.charAt(0).toUpperCase() ||
                             userData?.companyName?.charAt(0).toUpperCase() ||
                             'U'}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap component="div" sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.95rem' }}>
                                {userData?.companyName || userData?.displayName || currentUser?.email || 'User'}
                            </Typography>
                            <Typography noWrap component="div" sx={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.75rem' }}>
                                {currentUser?.email || 'user@company.com'}
                            </Typography>
                            <Chip
                                label={userRole || 'User'}
                                size="small"
                                sx={{
                                    mt: 0.5,
                                    height: 18,
                                    fontSize: '0.5rem',
                                    bgcolor: `${palette.rust}12`,
                                    color: getReadableColor(palette.rust),
                                    fontWeight: 600,
                                    borderRadius: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {profileMenuItems.filter(item => item.show !== false).map((item) => (
                    <MenuItem
                        key={item.label}
                        onClick={() => {
                            handleProfileClose();
                            navigate(item.path);
                        }}
                        sx={{
                            py: 1.2,
                            px: 2.5,
                            gap: 2,
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            color: 'rgba(0,0,0,0.8)',
                            '& .MuiSvgIcon-root': { color: 'inherit' },
                            '&:hover': { backgroundColor: `${palette.rust}10`, color: palette.rust }
                        }}
                    >
                        {item.icon}
                        {item.label}
                    </MenuItem>
                ))}

                <Divider sx={{ borderColor: palette.hairline }} />
                <MenuItem
                    onClick={handleLogout}
                    className="logout-item"
                    sx={{
                        py: 1.2,
                        px: 2.5,
                        gap: 2,
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: '#ef4444',
                        '& .MuiSvgIcon-root': { color: 'inherit' },
                        '&:hover': { backgroundColor: 'rgba(239,68,68,0.08)' }
                    }}
                >
                    <LogoutIcon sx={{ fontSize: 22 }} />
                    Logout
                </MenuItem>
            </Menu>

            {/* Mobile Menu */}
            <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '20px',
                            ...GLASS_POPOVER,
                            minWidth: 320,
                            mt: 0.6,
                            overflow: 'hidden',
                        }
                    }
                }}
                TransitionComponent={Fade}
            >
                <Box sx={{
                    p: 2,
                    borderBottom: `1px solid ${palette.hairline}`,
                    background: 'linear-gradient(135deg, rgba(var(--primary-rgb),.055), rgba(var(--accent-rgb),.035))',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar src={userData?.profileImage || currentUser?.photoURL || undefined} sx={{
                            width: 40,
                            height: 40,
                            background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: '#fff',
                            boxShadow: '0 2px 12px rgba(var(--primary-rgb),.18)',
                        }}>
                            {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap component="div" sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.9rem' }}>
                                {userData?.companyName || 'User'}
                            </Typography>
                            <Typography noWrap component="div" sx={{ color: 'rgba(0,0,0,0.4)', fontSize: '0.7rem' }}>
                                {currentUser?.email || ''}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {navItems.map((item) => (
                    <MenuItem
                        key={item.label}
                        onClick={() => {
                            handleMobileMenuClose();
                            navigate(item.path);
                        }}
                        sx={{
                            py: 1.2,
                            px: 2.5,
                            gap: 2,
                            fontSize: '0.85rem',
                            fontWeight: isActive(item.path) ? 700 : 500,
                            color: isActive(item.path) ? palette.rust : 'rgba(0,0,0,0.8)',
                            backgroundColor: isActive(item.path) ? `${palette.rust}08` : 'transparent',
                            '& .MuiSvgIcon-root': { color: 'inherit' },
                            '&:hover': { backgroundColor: `${palette.rust}10`, color: palette.rust }
                        }}
                    >
                        {item.icon}
                        {item.label}
                    </MenuItem>
                ))}

                <Divider sx={{ borderColor: palette.hairline }} />
                <MenuItem
                    onClick={() => {
                        handleMobileMenuClose();
                        navigate('/dashboard/company-info');
                    }}
                    sx={{ py: 1.2, px: 2.5, gap: 2, fontSize: '0.85rem', color: 'rgba(0,0,0,0.8)', '& .MuiSvgIcon-root': { color: 'inherit' } }}
                >
                    <AccountCircleIcon sx={{ fontSize: 22 }} />
                    My Profile
                </MenuItem>
                {!isTeamMemberRole && (isCompanyAdmin || userRole === 'admin') && (
                    <MenuItem
                        onClick={() => {
                            handleMobileMenuClose();
                            navigate('/dashboard/company/team');
                        }}
                        sx={{ py: 1.2, px: 2.5, gap: 2, fontSize: '0.85rem', color: 'rgba(0,0,0,0.8)', '& .MuiSvgIcon-root': { color: 'inherit' } }}
                    >
                        <GroupIcon sx={{ fontSize: 22 }} />
                        Company Team
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => {
                        handleMobileMenuClose();
                        navigate('/dashboard/company/ai-insights');
                    }}
                    sx={{ py: 1.2, px: 2.5, gap: 2, fontSize: '0.85rem', color: 'rgba(0,0,0,0.8)', '& .MuiSvgIcon-root': { color: 'inherit' } }}
                >
                    <AiIcon sx={{ fontSize: 22 }} />
                    AI Insights
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleMobileMenuClose();
                        navigate('/dashboard/ads');
                    }}
                    sx={{ py: 1.2, px: 2.5, gap: 2, fontSize: '0.85rem', color: 'rgba(0,0,0,0.8)', '& .MuiSvgIcon-root': { color: 'inherit' } }}
                >
                    <RocketLaunchIcon sx={{ fontSize: 22 }} />
                    Ad Campaigns
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleMobileMenuClose();
                        navigate('/dashboard/reports');
                    }}
                    sx={{ py: 1.2, px: 2.5, gap: 2, fontSize: '0.85rem', color: 'rgba(0,0,0,0.8)', '& .MuiSvgIcon-root': { color: 'inherit' } }}
                >
                    <AssessmentIcon sx={{ fontSize: 22 }} />
                    Reports
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleMobileMenuClose();
                        navigate('/dashboard/company/settings');
                    }}
                    sx={{ py: 1.2, px: 2.5, gap: 2, fontSize: '0.85rem', color: 'rgba(0,0,0,0.8)', '& .MuiSvgIcon-root': { color: 'inherit' } }}
                >
                    <SettingsIcon sx={{ fontSize: 22 }} />
                    Settings
                </MenuItem>
                <Divider sx={{ borderColor: palette.hairline }} />
                <MenuItem
                    onClick={handleLogout}
                    sx={{
                        py: 1.2,
                        px: 2.5,
                        gap: 2,
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: '#ef4444',
                        '& .MuiSvgIcon-root': { color: 'inherit' },
                        '&:hover': { backgroundColor: 'rgba(239,68,68,0.08)' }
                    }}
                >
                    <LogoutIcon sx={{ fontSize: 22 }} />
                    Logout
                </MenuItem>
            </Menu>
        </AppBar>
        {/* Fixed AppBar spacer: every internal page renders Header itself, so the
            spacer prevents page heroes/KPIs from sliding underneath the top bar. */}
        <Box aria-hidden="true" sx={{ height: `${headerHeight}px`, flexShrink: 0, pointerEvents: 'none' }} />
        </>
    );
};

export default Header;